import { useEffect, useMemo, useState } from "react";
import { createClient } from "@supabase/supabase-js";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  Calendar,
  Users,
  Plus,
  RefreshCw,
  LogOut,
  Search,
  Pencil,
  Images,
} from "lucide-react";

// Dialogs shadcn/ui
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
  DialogClose,
} from "@/components/ui/dialog";

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL!,
  import.meta.env.VITE_SUPABASE_ANON_KEY!
);

const BUCKET_NAME = "checkins-fotos";

/* ================== TIPOS ================== */
type Desafio = {
  id: number;
  nome: string | null;
  nome_fantasia: string | null;
  data_inicio?: string;
  data_fim?: string;
};
type Usuario = {
  id: number;
  nome: string | null;
  cpf: string | null;
  contato: string | null;
  instagram: string | null;
};
type UsuarioDesafio = {
  usuario_id: number;
  desafio_id: number;
  usuarios: Usuario | null;
};
type Checkin = {
  id: string;
  usuario_id: number;
  desafio_id: number;
  created_at: string; // ISO
  foto_url: string[] | null; // ARRAY de URLs/paths
};

/* ================== HELPERS ================== */
const formatContato = (value: string) => {
  const clean = value.replace(/\D/g, "").slice(0, 11);
  return clean
    .replace(/^(\d{2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d)/, "$1-$2");
};
const formatCPF = (value: string) => {
  const clean = value.replace(/\D/g, "");
  return clean
    .slice(0, 11)
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
};
const isCPFMaskedValid = (cpf: string) =>
  /^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(cpf);

function toLocalISOString(date: Date) {
  const tzOffset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - tzOffset).toISOString();
}

// Formata pelo date-part (UTC) do ISO para não "voltar" um dia com fuso
const fmtPtBR = (iso: string) => {
  const ymd = (iso || "").slice(0, 10); // "YYYY-MM-DD"
  const [y, m, d] = ymd.split("-");
  return `${d}/${m}/${y}`;
};

/* ================== COMPONENTE ================== */
export default function AdminParticipantes() {
  const navigate = useNavigate();
  const { toast } = useToast();

  // base
  const [desafios, setDesafios] = useState<Desafio[]>([]);
  const [selectedDesafio, setSelectedDesafio] = useState<number | null>(null);

  // participantes
  const [participantes, setParticipantes] = useState<UsuarioDesafio[]>([]);
  const [loading, setLoading] = useState(false);

  // busca (controlada por botão)
  const [uiSearch, setUiSearch] = useState("");
  const [search, setSearch] = useState("");

  // modal cadastro
  const [openModal, setOpenModal] = useState(false);

  // cadastro
  const [nome, setNome] = useState("");
  const [cpf, setCpf] = useState("");
  const [contato, setContato] = useState("");
  const [instagram, setInstagram] = useState(""); // NEW
  const [saving, setSaving] = useState(false);

  // modal edição
  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [editNome, setEditNome] = useState("");
  const [editCpf, setEditCpf] = useState("");
  const [editContato, setEditContato] = useState("");
  const [editInstagram, setEditInstagram] = useState(""); // NEW
  const [editSaving, setEditSaving] = useState(false);

  // modal check-in manual
  const [openCheckInModal, setOpenCheckInModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedUser, setSelectedUser] = useState<Usuario | null>(null);
  const [checkinSaving, setCheckinSaving] = useState(false);
  const [userCheckins, setUserCheckins] = useState<Checkin[]>([]);

  // modal de posts/fotos
  const [postsOpen, setPostsOpen] = useState(false);
  const [postsLoading, setPostsLoading] = useState(false);
  const [postsUser, setPostsUser] = useState<Usuario | null>(null);
  const [posts, setPosts] = useState<
    { id: string; created_at: string; fotos: string[] }[]
  >([]);

  /* ====== LOAD INICIAL ====== */
  const loadDesafios = async () => {
    const { data, error } = await supabase
      .from("desafios")
      .select("id, nome, nome_fantasia, data_inicio, data_fim")
      .order("nome", { ascending: true }); // ordena por nome

    if (error) {
      toast({
        title: "Erro ao carregar desafios",
        description: error.message ?? "Tente novamente.",
        variant: "destructive",
      });
      return;
    }
    setDesafios((data ?? []) as Desafio[]);
    if ((data ?? []).length > 0 && !selectedDesafio) {
      setSelectedDesafio((data![0] as Desafio).id);
    }
  };

  useEffect(() => {
    loadDesafios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ====== CARREGAR PARTICIPANTES (somente por botão) ====== */
  const loadParticipantes = async (desafioId: number) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("usuarios_desafios")
        .select(
          `usuario_id, desafio_id,
           usuarios ( id, nome, cpf, contato, instagram )`
        )
        .eq("desafio_id", desafioId)
        .order("usuario_id", { ascending: true });

      if (error) throw error;
      setParticipantes((data ?? []) as UsuarioDesafio[]);
    } catch (err: any) {
      toast({
        title: "Erro ao carregar participantes",
        description: err?.message ?? "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  /* ====== CARREGAR CHECK-INS DE UM USUÁRIO ====== */
  const loadCheckinsForUser = async (userId: number) => {
    try {
      const { data, error } = await supabase
        .from("checkins")
        .select("id, created_at, usuario_id, desafio_id")
        .eq("usuario_id", userId)
        .eq("desafio_id", selectedDesafio)
        .order("created_at", { ascending: true });

      if (error) throw error;
      setUserCheckins(data as Checkin[]);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar check-ins",
        description: error?.message ?? "Tente novamente.",
        variant: "destructive",
      });
    }
  };

  /* ====== CARREGAR POSTS/FOTOS DO USUÁRIO ====== */
  const openPostsForUser = async (user: Usuario | null) => {
    if (!user || !selectedDesafio) return;
    setPostsUser(user);
    setPostsOpen(true);
    setPostsLoading(true);

    try {
      const { data, error } = await supabase
        .from("checkins")
        .select("id, created_at, foto_url")
        .eq("usuario_id", user.id)
        .eq("desafio_id", selectedDesafio)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // normaliza e assina URLs quando necessário
      const norm = await Promise.all(
        (data ?? []).map(async (row: any) => {
          const srcs: string[] = [];
          const arr: string[] = Array.isArray(row.foto_url) ? row.foto_url : [];
          for (const item of arr) {
            if (!item) continue;
            if (/^https?:\/\//i.test(item)) {
              srcs.push(item);
            } else {
              // path interno -> tenta assinar
              const { data: s, error: e } = await supabase.storage
                .from(BUCKET_NAME)
                .createSignedUrl(item, 60 * 60);
              srcs.push(e ? item : s?.signedUrl ?? item);
            }
          }
          return {
            id: row.id as string,
            created_at: row.created_at as string,
            fotos: srcs,
          };
        })
      );

      setPosts(norm);
    } catch (err: any) {
      toast({
        title: "Erro ao carregar posts",
        description: err?.message ?? "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setPostsLoading(false);
    }
  };

  const handleOpenCheckInModal = (user: Usuario) => {
    setSelectedUser(user);
    setOpenCheckInModal(true);
    loadCheckinsForUser(user.id);
  };

  /* ====== DERIVADOS ====== */
  const participantesFiltrados = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return participantes;
    return participantes.filter((p) => {
      const n = p.usuarios?.nome?.toLowerCase() ?? "";
      const c = p.usuarios?.cpf?.toLowerCase() ?? "";
      const i = p.usuarios?.instagram?.toLowerCase() ?? "";
      const t = p.usuarios?.contato?.toLowerCase() ?? "";
      return n.includes(q) || c.includes(q) || i.includes(q) || t.includes(q);
    });
  }, [participantes, search]);

  const totalParticipantes = participantes.length;

  // Normaliza e mantém somente posts com fotos válidas
  const photoPosts = useMemo(() => {
    return (posts ?? [])
      .map((p) => ({
        ...p,
        fotos: Array.isArray(p.fotos) ? p.fotos.filter(Boolean) : [],
      }))
      .filter((p) => p.fotos.length > 0);
  }, [posts]);

  /* ====== AÇÕES ====== */
  const handleLogout = () => navigate("/admin");
  const handleApplyFilter = () => setSearch(uiSearch);

  const resetForm = () => {
    setNome("");
    setCpf("");
    setContato("");
    setInstagram(""); // NEW
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDesafio) {
      toast({
        title: "Selecione um desafio",
        description: "Escolha o desafio antes de cadastrar.",
        variant: "destructive",
      });
      return;
    }
    if (!nome.trim()) {
      toast({
        title: "Nome obrigatório",
        description: "Informe o nome do participante.",
        variant: "destructive",
      });
      return;
    }
    if (!isCPFMaskedValid(cpf)) {
      toast({
        title: "CPF inválido",
        description: "Use o formato 000.000.000-00.",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const { data: upUser, error: upErr } = await supabase
        .from("usuarios")
        .upsert(
          [
            {
              nome: nome.trim(),
              cpf: cpf.trim(),
              contato: contato.trim(),
              instagram: instagram.trim() || null,
            },
          ],
          { onConflict: "cpf" }
        )
        .select("id, cpf")
        .single();

      if (upErr) throw upErr;
      const usuarioId = upUser!.id;

      const { error: linkErr } = await supabase
        .from("usuarios_desafios")
        .upsert([{ usuario_id: usuarioId, desafio_id: selectedDesafio }], {
          onConflict: "usuario_id,desafio_id",
        });

      if (linkErr) throw linkErr;

      toast({
        title: "Participante cadastrado!",
        description: "Usuário salvo e associado ao desafio.",
      });
      setOpenModal(false);
      resetForm();
    } catch (err: any) {
      toast({
        title: "Erro ao salvar participante",
        description:
          err?.code === "42501"
            ? "Este CPF já está inscrito neste desafio."
            : err?.message ?? "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (u: Usuario | null) => {
    if (!u) return;
    setEditId(u.id);
    setEditNome(u.nome ?? "");
    setEditCpf(u.cpf ?? "");
    setEditContato(u.contato ?? "");
    setEditInstagram(u.instagram ?? ""); // NEW
    setEditOpen(true);
  };

  // função presente e usada no onSubmit do modal de edição
  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editId) return;

    if (!editNome.trim()) {
      toast({
        title: "Nome obrigatório",
        description: "Informe o nome do participante.",
        variant: "destructive",
      });
      return;
    }
    if (!isCPFMaskedValid(editCpf)) {
      toast({
        title: "CPF inválido",
        description: "Use o formato 000.000.000-00.",
        variant: "destructive",
      });
      return;
    }

    setEditSaving(true);
    try {
      const { error: updErr } = await supabase
        .from("usuarios")
        .update({
          nome: editNome.trim(),
          cpf: editCpf.trim(),
          contato: editContato.trim(),
          instagram: editInstagram.trim() || null, // NEW
        })
        .eq("id", editId);

      if (updErr) {
        const msg =
          (updErr as any)?.code === "23505"
            ? "Este CPF já está cadastrado para outro usuário."
            : (updErr as any)?.message ?? "Tente novamente.";
        throw new Error(msg);
      }

      // otimista
      setParticipantes((prev) =>
        prev.map((p) =>
          p.usuarios?.id === editId
            ? {
                ...p,
                usuarios: {
                  ...p.usuarios!,
                  nome: editNome,
                  cpf: editCpf,
                  contato: editContato,
                  instagram: editInstagram || null, // NEW
                },
              }
            : p
        )
      );

      toast({
        title: "Dados atualizados",
        description: "As informações do participante foram salvas.",
      });
      setEditOpen(false);
    } catch (err: any) {
      toast({
        title: "Erro ao atualizar",
        description: err?.message ?? "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setEditSaving(false);
    }
  };

  const handleCheckInSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser || !selectedDate || !selectedDesafio) {
      toast({
        title: "Erro",
        description: "Por favor, selecione uma data e um desafio.",
        variant: "destructive",
      });
      return;
    }
    setCheckinSaving(true);
    try {
      const d = new Date(selectedDate + "T15:00:00");
      const { error } = await supabase.from("checkins").insert([
        {
          usuario_id: selectedUser.id,
          desafio_id: selectedDesafio,
          created_at: toLocalISOString(d),
        },
      ]);
      if (error) throw error;
      toast({
        title: "Check-in realizado!",
        description: `Check-in registrado para ${selectedUser.nome}.`,
      });
      setOpenCheckInModal(false);
    } catch (error: any) {
      toast({
        title: "Erro ao registrar check-in",
        description: error?.message || "Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setCheckinSaving(false);
    }
  };

  /* ================== UI ================== */
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <header className="bg-white border-b shadow-sm mb-8">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-6">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-1xl sm:text-2xl font-bold bg-gradient-primary bg-clip-text text-transparent">
              Painel de Administração — Participantes
            </h1>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" /> Sair
            </Button>
          </div>
        </div>
      </header>

      {/* Seleção de desafio + ações */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="md:col-span-2">
          <CardHeader className="pb-2 px-3 sm:px-6">
            <CardTitle>Selecione um desafio</CardTitle>
            <CardDescription>
              Carregue a lista apenas ao clicar no botão
            </CardDescription>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="w-full sm:w-64">
                <Label className="mb-1 block">Desafio</Label>
                <Select
                  value={selectedDesafio ? String(selectedDesafio) : ""}
                  onValueChange={(v) => setSelectedDesafio(Number(v))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o desafio" />
                  </SelectTrigger>
                  <SelectContent>
                    {desafios.length === 0 ? (
                      <SelectItem value="0" disabled>
                        Sem desafios
                      </SelectItem>
                    ) : (
                      desafios.map((d) => (
                        <SelectItem key={d.id} value={String(d.id)}>
                          {d.nome ?? `Desafio #${d.id}`}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-end gap-3 flex-wrap">
                <Button
                  onClick={() =>
                    selectedDesafio && loadParticipantes(selectedDesafio)
                  }
                  disabled={!selectedDesafio || loading}
                  className="w-full sm:w-auto"
                >
                  <RefreshCw
                    className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
                  />
                  {loading ? "Carregando..." : "Carregar participantes"}
                </Button>

                {/* Abrir modal de cadastro */}
                <Dialog open={openModal} onOpenChange={setOpenModal}>
                  <DialogTrigger asChild>
                    <Button
                      disabled={!selectedDesafio}
                      className="bg-azul w-full sm:w-auto"
                    >
                      <Plus className="h-4 w-4 mr-2" /> Adicionar participante
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[520px]">
                    <DialogHeader>
                      <DialogTitle>Novo participante</DialogTitle>
                      <DialogDescription>
                        O usuário será criado/atualizado e vinculado ao desafio
                        selecionado.
                      </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleSave} className="space-y-4">
                      <div>
                        <Label htmlFor="nome">Nome</Label>
                        <Input
                          id="nome"
                          value={nome}
                          onChange={(e) => setNome(e.target.value)}
                          placeholder="Nome completo"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="cpf">CPF</Label>
                        <Input
                          id="cpf"
                          value={cpf}
                          onChange={(e) => setCpf(formatCPF(e.target.value))}
                          placeholder="000.000.000-00"
                          maxLength={14}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="contato">Contato</Label>
                        <Input
                          id="contato"
                          value={contato}
                          onChange={(e) =>
                            setContato(formatContato(e.target.value))
                          }
                          placeholder="(99) 99999-9999"
                          maxLength={15}
                          minLength={15}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="instagram">Instagram (opcional)</Label>
                        <Input
                          id="instagram"
                          value={instagram}
                          onChange={(e) => setInstagram(e.target.value)}
                          placeholder="@usuario"
                        />
                      </div>

                      <DialogFooter className="gap-2">
                        <DialogClose asChild>
                          <Button type="button" variant="outline">
                            Cancelar
                          </Button>
                        </DialogClose>
                        <Button
                          type="submit"
                          disabled={saving || !selectedDesafio}
                        >
                          {saving ? "Salvando..." : "Salvar"}
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 px-3 sm:px-6">
            <CardTitle>Resumo</CardTitle>
            <CardDescription>Dados do desafio selecionado</CardDescription>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Participantes
                </span>
              </div>
              <div className="text-3xl font-bold text-azul">
                {totalParticipantes}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista de participantes */}
      <Card>
        <CardHeader className="px-3 sm:px-6">
          <CardTitle>Participantes do desafio</CardTitle>

          {/* Busca controlada por botão */}
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <Label className="mb-2 block">
                Buscar (Nome, CPF, Insta, Contato)
              </Label>
              <div className="relative">
                <Input
                  placeholder="Digite para preparar o filtro"
                  value={uiSearch}
                  onChange={(e) => setUiSearch(e.target.value)}
                  className="pl-9"
                />
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              </div>
            </div>
            <div className="self-end">
              <Button variant="secondary" onClick={handleApplyFilter}>
                Aplicar filtro
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="px-3 sm:px-6">
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[30px] py-2 px-2">Nº</TableHead>
                  <TableHead className="py-2 px-2">Nome</TableHead>
                  <TableHead className="w-[130px] py-2 px-2">CPF</TableHead>
                  <TableHead className="w-[160px] py-2 px-2">Contato</TableHead>
                  <TableHead className="w-[130px] py-2 px-2">
                    Instagram
                  </TableHead>
                  {/* NEW */}
                  <TableHead className="w-[210px] py-2 px-2 text-right">
                    Ações
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {participantesFiltrados.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6} // +1 por causa do Instagram
                      className="text-center py-4 text-muted-foreground"
                    >
                      {loading
                        ? "Carregando..."
                        : "Nenhum participante listado. Selecione um desafio e clique em Carregar participantes."}
                    </TableCell>
                  </TableRow>
                ) : (
                  participantesFiltrados.map((p, index) => (
                    <TableRow key={`${p.desafio_id}-${p.usuario_id}`}>
                      <TableCell className="font-mono py-2 px-2">
                        {index + 1}
                      </TableCell>
                      <TableCell className="font-medium uppercase py-2 px-2">
                        {p.usuarios?.nome}{" "}
                        <span className="hidden">- {p.usuarios?.id}</span>
                      </TableCell>
                      <TableCell className="font-mono py-2 px-2">
                        {p.usuarios?.cpf}
                      </TableCell>
                      <TableCell className="font-mono py-2 px-2">
                        {p.usuarios?.contato}
                      </TableCell>
                      <TableCell className="font-mono py-2 px-2">
                        {p.usuarios?.instagram
                          ? p.usuarios.instagram.startsWith("@")
                            ? p.usuarios.instagram
                            : `@${p.usuarios.instagram}`
                          : ""}
                      </TableCell>
                      <TableCell className="py-2 px-2 text-right space-x-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleOpenCheckInModal(p.usuarios!)}
                          title="Registrar Check-in"
                        >
                          <Calendar className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openEdit(p.usuarios)}
                          title="Editar participante"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openPostsForUser(p.usuarios)}
                          title="Ver posts/fotos"
                          disabled={!selectedDesafio}
                        >
                          <Images className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Modal de edição */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Editar participante</DialogTitle>
            <DialogDescription>
              Atualize os dados do usuário e salve as alterações.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleUpdate} className="space-y-4">
            <div>
              <Label htmlFor="edit-nome">Nome</Label>
              <Input
                id="edit-nome"
                value={editNome}
                onChange={(e) => setEditNome(e.target.value)}
                placeholder="Nome completo"
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-cpf">CPF</Label>
              <Input
                id="edit-cpf"
                value={editCpf}
                onChange={(e) => setEditCpf(formatCPF(e.target.value))}
                placeholder="000.000.000-00"
                maxLength={14}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-contato">Contato</Label>
              <Input
                id="edit-contato"
                value={editContato}
                onChange={(e) => setEditContato(formatContato(e.target.value))}
                placeholder="(99) 99999-9999"
                maxLength={15}
                minLength={15}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-instagram">Instagram</Label>
              <Input
                id="edit-instagram"
                value={editInstagram}
                onChange={(e) => setEditInstagram(e.target.value)}
                placeholder="@usuario"
              />
            </div>

            <DialogFooter className="gap-2">
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  Cancelar
                </Button>
              </DialogClose>
              <Button type="submit" disabled={editSaving || !editId}>
                {editSaving ? "Salvando..." : "Salvar alterações"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de check-in manual */}
      <Dialog open={openCheckInModal} onOpenChange={setOpenCheckInModal}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Check-in do participante</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCheckInSubmit} className="space-y-2">
            <div>
              <Label>Nome: {selectedUser?.nome}</Label>
              <br />
              <Label>CPF: {selectedUser?.cpf}</Label>
            </div>

            <div className="pb-2">
              <Label>Check-ins já realizados</Label>
              <div className="flex flex-wrap gap-2 text-xs mt-2">
                {userCheckins.length === 0 ? (
                  <p>Sem check-ins realizados.</p>
                ) : (
                  [
                    ...new Set(
                      userCheckins.map(
                        (c) =>
                          new Date(c.created_at).toISOString().split("T")[0]
                      )
                    ),
                  ].map((d) => (
                    <span
                      key={d}
                      className="border border-gray-300 px-2 py-1 rounded-sm"
                    >
                      {fmtPtBR(d)}
                    </span>
                  ))
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="check-in-date">Data do Check-in</Label>
              <Input
                id="check-in-date"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                required
                min={
                  desafios.find((des) => des.id === selectedDesafio)
                    ?.data_inicio
                }
                max={
                  desafios.find((des) => des.id === selectedDesafio)?.data_fim
                }
              />
              {userCheckins.some(
                (c) =>
                  new Date(c.created_at).toISOString().split("T")[0] ===
                  selectedDate
              ) && (
                <p className="text-red-500 text-xs mt-2">
                  Este usuário já realizou um check-in na data selecionada.
                </p>
              )}
            </div>

            <DialogFooter className="gap-2">
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  Cancelar
                </Button>
              </DialogClose>
              <Button
                type="submit"
                disabled={
                  checkinSaving ||
                  userCheckins.some(
                    (c) =>
                      new Date(c.created_at).toISOString().split("T")[0] ===
                      selectedDate
                  )
                }
              >
                {checkinSaving ? "Salvando..." : "Fazer check-in"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de posts/fotos */}
      <Dialog open={postsOpen} onOpenChange={setPostsOpen}>
        <DialogContent className="sm:max-w-[780px]">
          <DialogHeader>
            <DialogTitle>Posts do participante</DialogTitle>
            <DialogDescription>
              {postsUser
                ? `${postsUser.nome}${
                    postsUser?.instagram
                      ? ` — Instagram: ${
                          postsUser.instagram.startsWith("@")
                            ? postsUser.instagram
                            : "@" + postsUser.instagram
                        }`
                      : ""
                  }`
                : "Selecione um participante"}
            </DialogDescription>
          </DialogHeader>

          {postsLoading ? (
            <div className="py-8 text-center text-muted-foreground">
              Carregando posts...
            </div>
          ) : photoPosts.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              Este participante não tem posts com fotos neste desafio.
            </div>
          ) : (
            <div className="space-y-6 max-h-[70vh] overflow-auto pr-1">
              {photoPosts.map((p) => (
                <div key={p.id} className="border rounded-lg p-3">
                  <div className="text-sm font-medium mb-2">
                    {fmtPtBR(p.created_at)}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {p.fotos.map((src: string, i: number) => (
                      <a
                        key={i}
                        href={src}
                        target="_blank"
                        rel="noreferrer"
                        className="block rounded-md overflow-hidden ring-2 ring-transparent hover:ring-primary transition"
                        title="Abrir em nova aba"
                      >
                        <img
                          src={src}
                          alt={`Foto ${i + 1}`}
                          className="h-28 w-full object-cover"
                        />
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Fechar</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Acesso rápido */}
      <div className="mt-8 flex justify-center">
        <Button
          variant="outline"
          size="lg"
          onClick={() => navigate("/admin")}
          className="max-w-xs"
        >
          <Calendar className="w-4 h-4 mr-2" /> Voltar ao Dashboard
        </Button>
      </div>
    </div>
  );
}
